import type { Problem } from '@myrewards/contracts';

/** Codes the client gives errors that never reached a problem+json answer. */
export type ClientErrorCode =
  /** The browser couldn't reach the server (offline, DNS, connection reset). */
  | 'network_error'
  /** The server answered with something that isn't problem+json (a proxy's HTML 502, say). */
  | 'unexpected_response'
  /** The server answered 2xx, but the body doesn't match the contract. */
  | 'invalid_response';

/**
 * Every failed API call becomes one of these: the HTTP status (0 when the server was never
 * reached) and a stable `code` the UI maps to friendly copy (`not_signed_in`, `forbidden`, …).
 */
export class ApiError extends Error {
  override readonly name = 'ApiError';
  readonly status: number;
  readonly code: string;
  readonly requestId: string | null;
  /** The parsed problem+json, when the server sent one. */
  readonly problem: Problem | null;

  constructor(
    status: number,
    code: string,
    message: string,
    options: { problem?: Problem; cause?: unknown; requestId?: string | null } = {},
  ) {
    super(message, options.cause === undefined ? undefined : { cause: options.cause });
    this.status = status;
    this.code = code;
    this.problem = options.problem ?? null;
    this.requestId = options.requestId ?? null;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/** The session is missing or has ended: the server answered 401. */
export function isNotSignedIn(error: unknown): boolean {
  return isApiError(error) && error.status === 401;
}

/** A 4xx answer: retrying the same request won't change it. */
export function isClientError(error: unknown): boolean {
  return isApiError(error) && error.status >= 400 && error.status < 500;
}
